import { PhoneIcon } from '@chakra-ui/icons'
import {
  Button,
  ButtonProps,
  Flex,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Text,
} from '@chakra-ui/react'
import { useApp, useAuth, usePopUpMessage, useUserGroup } from '@hb/react'
import {
  Assessment,
  DateField,
  ErrorWithMessage,
  FieldTypes,
  getDateString,
  ScheduledCall,
  UserGroup,
} from '@hb/shared'
import { DocumentReference, serverTimestamp, updateDoc } from 'firebase/firestore'
import { useCallback, useMemo, useState } from 'react'
import { useProfile } from '../../../contexts/user/ProfileContext'
import { SolidActionButton } from '../../Buttons'
import { UserBadge } from '../../Users/UserBadge'
import { StandaloneInput } from '../../forms/Input'

const reportCallComplete = async (
  uid: string,
  group: UserGroup,
  assessmentRef: DocumentReference<Assessment>,
  scheduledCall: ScheduledCall | null | undefined,
  callDate: number,
) =>
  updateDoc(assessmentRef, 'scheduledCall', {
    ...scheduledCall,
    callDate,
    reportedBy: uid,
    reportedByGroup: group,
    reportedOn: serverTimestamp(),
  })

const unreportCall = async (
  assessmentRef: DocumentReference<Assessment>,
  scheduledCall: ScheduledCall | null | undefined,
) =>
  updateDoc(assessmentRef, 'scheduledCall', {
    ...scheduledCall,
    callDate: null,
    reportedBy: null,
    reportedByGroup: null,
    reportedOn: null,
  })

const useOnReportCall = () => {
  const {
    selectedAssessment: { ref: assessmentRef, populated: selectedAssessment },
  } = useProfile()
  const { scheduledCall } = selectedAssessment ?? {}
  const authUser = useAuth(s => s.authUser)
  const userGroup = useUserGroup()
  const { showError, showSuccess } = usePopUpMessage()
  const [reportCompleteLoading, setReportCompleteLoading] = useState(false)
  const [unreportLoading, setUnreportLoading] = useState(false)
  const onReportCallComplete = useCallback(
    async (callDate: number | null) => {
      const uid = authUser?.uid ?? ''
      const onError = (error: string) => {
        showError('Error reporting call as complete', error)
      }
      if (!uid) {
        onError('Not signed in')
        return
      }
      if (!assessmentRef) {
        onError('No assessment selected')
        return
      }
      if (!callDate) {
        onError('No call date provided')
        return
      }
      try {
        setReportCompleteLoading(true)
        await reportCallComplete(uid, userGroup, assessmentRef, scheduledCall, callDate)
        showSuccess('Call reported as scheduled')
      } catch (error) {
        showError(
          `Error reporting call as scheduled: ${(error as ErrorWithMessage).message ?? 'Unknown error'}`,
        )
      } finally {
        setReportCompleteLoading(false)
      }
    },
    [authUser, assessmentRef, userGroup, showError, showSuccess, scheduledCall],
  )

  const onUnreportCall = useCallback(async () => {
    const onError = (error: string) => {
      showError('Error unreporting call', error)
    }

    if (!assessmentRef) {
      onError('No assessment selected')
      return
    }
    if (!scheduledCall) {
      onError('No scheduled call found')
      return
    }
    try {
      setUnreportLoading(true)
      await unreportCall(assessmentRef, scheduledCall)
      showSuccess('Call unreported')
    } catch (error) {
      showError(`Error unreporting call: ${(error as ErrorWithMessage).message ?? 'Unknown error'}`)
    } finally {
      setUnreportLoading(false)
    }
  }, [assessmentRef, showError, showSuccess, scheduledCall])

  return {
    onReportCallComplete,
    onUnreportCall,
    reportCompleteLoading,
    unreportLoading,
  }
}

const callDateField: DateField = {
  placeholder: 'Call Date',
  type: FieldTypes.DATE,
  defaultToNow: true,
}

const ScheduledCallDetails = ({
  scheduledCall,
  onReportCallComplete,
  reportCompleteLoading,
  onUnreportCall,
  unreportLoading,
  readOnly,
}: {
  scheduledCall: ScheduledCall | null | undefined
  onReportCallComplete: (callDate: number | null) => void
  reportCompleteLoading: boolean
  onUnreportCall: () => void
  unreportLoading: boolean
  readOnly: boolean
}) => {
  const { callDate, reportedBy, reportedByGroup, reportedOn } = scheduledCall ?? {}
  const [tempCallDate, setTempCallDate] = useState<number | null>(null)
  return (
    <Flex w="100%" flexFlow="column">
      {callDate ? (
        <Flex flexFlow="column" w="100%">
          <Text fontWeight={500}>Call occurred on {getDateString(callDate, 'short')}</Text>
          <Flex align="center" w="100%" gap={2}>
            {reportedOn ? (
              <Text flex={1}>Reported on {getDateString(reportedOn, 'short')}</Text>
            ) : null}
            {reportedBy ? <UserBadge userId={reportedBy} userGroup={reportedByGroup} /> : null}
          </Flex>
          {!readOnly ? (
            <SolidActionButton
              mt={2}
              size="sm"
              onClick={onUnreportCall}
              isLoading={unreportLoading}>
              Unreport Call
            </SolidActionButton>
          ) : null}
        </Flex>
      ) : (
        <Flex w="100%" flexFlow="column" gap={2}>
          <Text color="gray.700" fontWeight={500}>
            {!readOnly ? 'Report call date' : 'Awaiting call'}
          </Text>
          {!readOnly ? (
            <>
              <StandaloneInput
                theme="detailed"
                field={callDateField}
                value={tempCallDate}
                onChange={setTempCallDate}
              />
              <SolidActionButton
                size="sm"
                onClick={() => onReportCallComplete(tempCallDate)}
                isLoading={reportCompleteLoading}>
                Report Call Date
              </SolidActionButton>
            </>
          ) : null}
        </Flex>
      )}
    </Flex>
  )
}

export const ScheduledCallButton = ({ buttonProps }: { buttonProps?: ButtonProps }) => {
  const {
    selectedAssessment: { populated: selectedAssessment },
  } = useProfile()
  const { appName } = useApp()
  const { scheduledCall } = selectedAssessment ?? {}
  const { scheduledOn, callDate } = scheduledCall ?? {}
  const { text, color } = useMemo(() => {
    if (callDate) {
      return { text: 'Admin Call', color: 'green' }
    }
    if (scheduledOn) {
      return { text: 'Scheduled By Patient', color: 'yellow' }
    }
    return { text: 'No Call Scheduled', color: 'gray' }
  }, [callDate, scheduledOn])

  const { onReportCallComplete, reportCompleteLoading, unreportLoading, onUnreportCall } =
    useOnReportCall()

  return (
    <Popover closeOnBlur={false} trigger="hover" placement="bottom-end" strategy="fixed">
      <PopoverTrigger>
        <Button
          isLoading={reportCompleteLoading || unreportLoading}
          size="sm"
          bg={`${color}.200`}
          minW="150px"
          // w={isMobile ? '100%' : undefined}
          // w="100%"
          color={`${color}.800`}
          leftIcon={<PhoneIcon />}
          _hover={{
            bg: `${color}.400`,
          }}
          {...buttonProps}>
          <Text textAlign="left" flex={1}>
            {text}
          </Text>
          {callDate ? (
            <Text ml="auto" fontSize="sm" fontWeight={600} color={`${color}.800`}>
              ✓
            </Text>
          ) : null}
        </Button>
      </PopoverTrigger>
      <PopoverContent bg="gray.50">
        <PopoverBody>
          <ScheduledCallDetails
            scheduledCall={scheduledCall}
            onReportCallComplete={onReportCallComplete}
            reportCompleteLoading={reportCompleteLoading}
            onUnreportCall={onUnreportCall}
            unreportLoading={unreportLoading}
            readOnly={appName === 'providers-app'}
          />
        </PopoverBody>
      </PopoverContent>
    </Popover>
  )
}
